import React, { useEffect, useState } from "react";
import {
  Form,
  Row,
  Column,
  TextInput,
  Button,
  ButtonSet,
  Tag,
} from "carbon-components-react";
import { Undo24 } from "@carbon/icons-react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Card from "../../components/Card/Card";
import CancelButton from "../../components/Buttons/CancelButton";
import http from "../../http-common";
import useAuth from "../../hooks/useAuth";
import { prototypeScoreSchema } from "../../services/SchemaService";

const SCORING_DOMAINS = [
  {
    code: "PF",
    label: "Adequação Funcional",
    weight: 20,
    criteria: [
      {
        code: "PF101",
        name: "Requisitos Essenciais",
        pesoInterno: 20,
        resultadoEsperado:
          "O sistema implementa integralmente todos os requisitos funcionais críticos definidos no escopo, permitindo ao utilizador concluir a tarefa principal sem bloqueios, omissões ou erros funcionais.",
        procedimento:
          "Abrir o sistema e identificar a tarefa principal definida no escopo. Executar a tarefa do início ao fim, seguindo o fluxo normal de utilização. Confirmar que a tarefa é concluída sem bloqueios, erros ou funcionalidades faltantes.",
        descricoes: [
          "0–4: 0–20% das funcionalidades críticas implementadas e operacionais; quase nenhuma função funciona.",
          "5–9: 40% das funcionalidades críticas implementadas e operacionais; várias funções com erros.",
          "10–14: 41–70% das funcionalidades principais implementadas e operacionais; algumas funcionalidades ainda ausentes.",
          "15–18: 71–95% das funcionalidades completas implementadas; erros ocasionais.",
          "19–20: 100% das funcionalidades implementadas e operacionais, sem erros.",
        ],
      },
    ],
  },
  {
    code: "PD",
    label: "Eficiência de Desempenho",
    weight: 10,
    criteria: [
      {
        code: "PD101",
        name: "Tempo de Resposta",
        pesoInterno: 5,
        resultadoEsperado:
          "O sistema executa operações principais dentro de tempos de resposta aceitáveis, sem atrasos perceptíveis ao utilizador, em ambiente de conexão estável.",
        procedimento:
          "Abrir o sistema e fazer login, confirmar que entra rapidamente. Executar operações principais (ex.: abrir páginas, submeter formulários, pesquisar dados). Verificar se as respostas são rápidas e sem atrasos perceptíveis durante o uso normal.",
        descricoes: [
          "0–4: >10 segundos em operações básicas.",
          "5–9: 5–10 segundos.",
          "10–14: 2–5 segundos.",
          "15–18: 1–2 segundos.",
          "19–20: <1 segundo em todas as funcionalidades.",
        ],
      },
      {
        code: "PD102",
        name: "Teste com Carga",
        pesoInterno: 5,
        resultadoEsperado:
          "O sistema mantém estabilidade, desempenho consistente e integridade de dados sob múltiplos acessos simultâneos, dentro do volume de utilizadores esperado para o contexto do projeto.",
        procedimento:
          "Reunir no mínimo 10 utilizadores e combinar para aceder ao sistema ao mesmo tempo. Todos devem abrir o link e fazer login simultaneamente, confirmando que entram sem erros. Navegar e realizar ações simples durante 10–15 minutos, verificando lentidão ou bloqueios. Confirmar que as ações realizadas foram guardadas corretamente.",
        descricoes: [
          "0–4: Falha com 2 utilizadores; sistema não permite login ou bloqueia rapidamente.",
          "5–9: Suporta 4 utilizadores simultaneamente, mas apresenta lentidão ou pequenos erros.",
          "10–14: Suporta 6 utilizadores simultaneamente com pequenas falhas.",
          "15–18: Suporta 8 utilizadores simultaneamente de forma estável.",
          "19–20: Suporta 10 utilizadores simultaneamente sem impacto; todas as ações são salvas corretamente.",
        ],
      },
    ],
  },
  {
    code: "PC",
    label: "Compatibilidade",
    weight: 10,
    criteria: [
      {
        code: "PC101",
        name: "Navegadores",
        pesoInterno: 5,
        resultadoEsperado:
          "O sistema apresenta funcionamento consistente, layout adequado e ausência de falhas críticas nos principais navegadores suportados.",
        procedimento:
          "Abrir o sistema nos principais navegadores suportados (Google Chrome, Microsoft Edge, Firefox, Opera Mini) e confirmar que o layout e funcionamento estão corretos, sem falhas críticas.",
        descricoes: [
          "0–4: Não funciona em navegadores populares (Chrome, Firefox, Edge, Opera Mini ou Safari).",
          "5–9: Funciona parcialmente em 1–2 navegadores.",
          "10–14: Funciona em 3 navegadores com erros visuais.",
          "15–18: Funciona em todos os navegadores, com pequenas diferenças.",
          "19–20: Comportamento idêntico em todos os navegadores (Chrome, Firefox, Edge, Opera Mini e Safari).",
        ],
      },
      {
        code: "PC102",
        name: "Dispositivos",
        pesoInterno: 5,
        resultadoEsperado:
          "O sistema adapta corretamente a interface e funcionalidades a diferentes dispositivos (desktop, tablet e smartphone), mantendo usabilidade e desempenho adequados.",
        procedimento:
          "Testar o sistema em 3 tipos de dispositivos: desktop, tablet e smartphone. Verificar se a interface e funcionalidades continuam usáveis e consistentes em cada dispositivo. Observar qualquer erro, lentidão ou problema de usabilidade.",
        descricoes: [
          "0–4: Apenas 1/3 dos dispositivos funciona corretamente (desktop); tablet e smartphone apresentam falhas críticas.",
          "5–9: 2/3 dos dispositivos funcionam (desktop e tablet); smartphone apresenta erros ou limitações.",
          "10–14: 1 dispositivo ou mais funciona sem falhas graves, mas nem todos os dispositivos testados operam perfeitamente.",
          "15–18: Todos os dispositivos (3/3) funcionam; pequenas falhas toleráveis (≤10% de problemas menores por dispositivo).",
          "19–20: Todos os dispositivos (3/3) funcionam perfeitamente, sem erros ou problemas de usabilidade.",
        ],
      },
    ],
  },
  {
    code: "PU",
    label: "Usabilidade",
    weight: 15,
    criteria: [
      {
        code: "PU101",
        name: "Interface do Utilizador",
        pesoInterno: 7.5,
        resultadoEsperado:
          "A interface permite que o utilizador compreenda rapidamente as funcionalidades e complete tarefas sem necessidade de suporte externo ou instruções adicionais. Aspetos como: interface intuitiva, fácil aprendizagem, consistência gráfica, clareza e estética.",
        procedimento:
          "Para página web: abrir o sistema nas versões recentes dos navegadores e sistemas operativos suportados. Para mobile apps: executar funções principais e verificar se tudo funciona corretamente. Observar se o utilizador consegue completar tarefas sem suporte externo.",
        descricoes: [
          "0–4: Utilizador consegue completar 0–20% das tarefas sem erros ou ajuda; interface extremamente confusa.",
          "5–9: Utilizador completa 21–40% das tarefas; problemas de leitura, interação ou compreensão.",
          "10–14: Utilizador completa 41–70% das tarefas; funcional, mas baixa ergonomia e usabilidade limitada.",
          "15–18: Utilizador completa 71–95% das tarefas; interface intuitiva e design visual agradável; pequenas melhorias possíveis.",
          "19–20: Utilizador completa 96–100% das tarefas sem ajuda; interface totalmente intuitiva e excelente design visual.",
        ],
      },
      {
        code: "PU102",
        name: "Navegação",
        pesoInterno: 7.5,
        resultadoEsperado:
          "A estrutura de navegação é lógica, organizada e intuitiva, permitindo fluxo contínuo entre funcionalidades sem confusão ou retrabalho. Por exemplo: menus, botões e caminhos bem definidos.",
        procedimento:
          "Abrir o sistema e explorar diferentes páginas ou secções. Mover-se entre funcionalidades seguindo o fluxo natural de uso. Confirmar que a navegação é lógica, organizada e intuitiva, sem confusão ou necessidade de voltar atrás.",
        descricoes: [
          "0–4: Utilizador consegue navegar corretamente em 0–20% das secções; menus não funcionam ou estão desorganizados.",
          "5–9: Utilizador consegue navegar em 21–40% das secções; menus funcionam, mas é difícil entender o fluxo.",
          "10–14: Utilizador navega corretamente em 41–70% das secções; navegação clara, mas com pontos de melhoria.",
          "15–18: Utilizador navega em 71–95% das secções de forma fluida e intuitiva; pequenas melhorias possíveis.",
          "19–20: Utilizador navega em 96–100% das secções perfeitamente, sem confusão ou retrabalho.",
        ],
      },
    ],
  },
  {
    code: "PR",
    label: "Confiabilidade",
    weight: 10,
    criteria: [
      {
        code: "PR101",
        name: "Tolerância a Falhas",
        pesoInterno: 10,
        resultadoEsperado:
          "O sistema trata erros de forma controlada, evitando travamentos, perda de dados ou interrupções críticas, e garantindo recuperação adequada quando ocorrem falhas.",
        procedimento:
          "Abrir o sistema e fazer login normalmente. Realizar tarefas típicas (ex.: criar registos, submeter formulários). Inserir dados incorretos ou incompletos e verificar mensagens de erro controladas. Confirmar que não há perda de dados ou travamentos. Observar durante o teste se falhas são raras e o sistema permanece estável.",
        descricoes: [
          "0–4: Sistema trava em mais de 80% das ações, sem recuperação automática.",
          "5–9: Sistema recupera parcialmente ou lentamente em 50–80% das falhas.",
          "10–14: Sistema recupera com falhas menores em 30–50% das situações; impacto limitado ao utilizador.",
          "15–18: Sistema recupera rápido e eficaz em 80–95% das situações, com mínimo impacto.",
          "19–20: Sistema recupera automaticamente em 96–100% das situações, sem impacto perceptível ao utilizador.",
        ],
      },
    ],
  },
  {
    code: "PS",
    label: "Segurança",
    weight: 15,
    criteria: [
      {
        code: "PS101",
        name: "Controlo de Acesso",
        pesoInterno: 15,
        resultadoEsperado:
          "O sistema restringe acesso a funcionalidades e dados com base em autenticação e níveis de permissão adequadamente definidos.",
        procedimento:
          "Fazer login com diferentes perfis de utilizador e verificar acesso às funcionalidades. Tentar aceder a áreas ou ações não autorizadas e confirmar que o sistema bloqueia. Confirmar que cada utilizador só consegue realizar ações permitidas pelo seu nível de permissão.",
        descricoes: [
          "0–4: Nenhum bloqueio; utilizadores conseguem aceder a qualquer funcionalidade sem autenticação.",
          "5–9: Apenas 1 de 3 tentativas de acesso não autorizado é bloqueada; autenticação fraca.",
          "10–14: 2 de 3 tentativas de acesso não autorizado bloqueadas; controlo básico com falhas menores.",
          "15–18: Todas as tentativas de acesso não autorizado são bloqueadas; autenticação confiável.",
          "19–20: Todas as tentativas bloqueadas, com autenticação multifator e registo de auditoria funcionando corretamente.",
        ],
      },
    ],
  },
  {
    code: "PM",
    label: "Manutenção",
    weight: 10,
    criteria: [
      {
        code: "PM101",
        name: "Continuidade",
        pesoInterno: 10,
        resultadoEsperado:
          "Atualizações ou alterações no sistema não comprometem dados previamente inseridos nem afetam a operação normal para o utilizador final.",
        procedimento:
          "Realizar uma atualização ou alteração simples no sistema (ex.: alterar um registo de teste, adicionar um novo item, ou mudar uma configuração). Verificar se os dados já existentes permanecem corretos e intactos. Confirmar que o sistema continua a operar normalmente.",
        descricoes: [
          "0–4: 3–5 problemas graves durante a atualização; sistema fica inoperante.",
          "5–9: 2 problemas visíveis durante a atualização; falhas percetíveis ao utilizador.",
          "10–14: 1 problema leve; pequenas interrupções ou falhas sem impacto crítico.",
          "15–18: 0–1 pequeno problema; atualização ocorre com estabilidade e impacto mínimo.",
          "19–20: 0 problemas; atualização transparente ao utilizador, sem interrupção ou perda de dados.",
        ],
      },
    ],
  },
  {
    code: "PP",
    label: "Portabilidade",
    weight: 10,
    criteria: [
      {
        code: "PP101",
        name: "Plano de Portabilidade",
        pesoInterno: 10,
        resultadoEsperado:
          "Capacidade de transferir e utilizar o protótipo em diferentes plataformas ou ambientes (desenvolvimento, teste, produção), assegurando flexibilidade.",
        procedimento:
          "Tratando-se de Protótipos Mais Viáveis, a ênfase da avaliação vai incidir sobre os planos existentes para garantir a portabilidade.",
        descricoes: [
          "0–4: Não existe descrição ou plano de portabilidade no guia/manual do protótipo.",
          "5–9: Existe descrição limitada do plano de portabilidade no guia/manual do protótipo, que não permite aferir a portabilidade da plataforma.",
          "10–14: Existe descrição limitada do plano de portabilidade no guia/manual do protótipo, que permite aferir a portabilidade da plataforma do ambiente de desenvolvimento para o de teste.",
          "15–18: Existe descrição detalhada do plano de portabilidade no guia/manual do protótipo.",
          "19–20: Detalhadamente descritos os planos de garantir portabilidade nos ambientes de desenvolvimento, teste e produção.",
        ],
      },
    ],
  },
];

const PERFORMANCE_SCALE = [
  { label: "Fraco", range: "0 – 4" },
  { label: "Razoável", range: "5 – 9" },
  { label: "Bom", range: "10 – 14" },
  { label: "Muito Bom", range: "15 – 18" },
  { label: "Excelente", range: "19 – 20" },
];

const ALL_CODES = SCORING_DOMAINS.flatMap((d) => d.criteria.map((c) => c.code));

const calcWeightedTotal = (values) => {
  let total = 0;
  SCORING_DOMAINS.forEach((domain) => {
    domain.criteria.forEach((c) => {
      const val = parseFloat(values[c.code]);
      if (!isNaN(val)) {
        total += (val / 20) * c.pesoInterno;
      }
    });
  });
  return total;
};

const formatDate = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const ScorePrototypePage = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [previousScore, setPreviousScore] = useState(null);
  const [previousEvaluationDate, setPreviousEvaluationDate] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(prototypeScoreSchema),
    mode: "onBlur",
  });

  const watchedValues = useWatch({ control, defaultValue: {} });
  const liveTotal = calcWeightedTotal(watchedValues);

  useEffect(() => {
    const fetchExistingScores = async () => {
      if (!user?.username) return;
      try {
        const response = await http.get(
          `/prototypes/${uuid}/scores/${user.username}`
        );
        if (response.data?.isScored && response.data?.criteriaScores) {
          const formData = {};
          response.data.criteriaScores.forEach((c) => {
            formData[c.criteriaCode] = c.score;
            formData[c.criteriaCode + "Remarks"] = c.remarks || "";
          });
          reset(formData);
          setPreviousScore(response.data.totalScore);
          setPreviousEvaluationDate(response.data.evaluationDate);
          toast.info("Pontuação anterior carregada");
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          toast.error("Erro ao carregar pontuação anterior");
        }
      }
    };
    fetchExistingScores();
  }, [uuid, user, reset]);

  const onSubmit = async (data) => {
    try {
      data.uuid = uuid;
      data.username = user.username;
      await http.post("/prototypes/score", data);
      const agora = new Date();
      setPreviousScore(liveTotal);
      setPreviousEvaluationDate(formatDate(agora));
      toast.success("Pontuação guardada com sucesso");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao submeter pontuação");
    }
  };

  const cardTitle =
    previousScore !== null && previousEvaluationDate !== null
      ? `Pontuar Protótipo — Avaliação Fase 2 - (Pontuação anterior: ${Number(previousScore).toFixed(2)} pts - ${previousEvaluationDate})`
      : "Pontuar Protótipo — Avaliação Fase 2";

  return (
    <Card title={cardTitle}>
      <div className="mb-3">
        <p className="mb-1">
          <strong>Instruções:</strong> Atribua uma pontuação de 0 a 20 para
          cada critério conforme a escala de desempenho:
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {PERFORMANCE_SCALE.map((s) => (
            <Tag key={s.label} type="gray">
              {s.range}: {s.label}
            </Tag>
          ))}
        </div>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)}>
        {SCORING_DOMAINS.map((domain) => (
          <div className="mt-2 mb-4" key={domain.code}>
            <h4 style={{ borderBottom: "1px solid #dedede", paddingBottom: "0.4rem" }}>
              {domain.label}{" "}
              <span style={{ fontWeight: "normal", fontSize: "0.9rem", color: "#525252" }}>
                (Peso: {domain.weight}%)
              </span>
            </h4>

            {domain.criteria.map((c) => (
              <div key={c.code} className="mt-2 mb-3">
                <Row>
                  <Column sm={12} md={12} lg={12}>
                    <p className="mt-1 mb-1">
                      <strong>
                        {c.code} — {c.name}
                      </strong>{" "}
                      <span style={{ fontSize: "0.85rem", color: "#525252" }}>
                        (Peso interno: {c.pesoInterno}%)
                      </span>
                    </p>
                    <p
                      style={{
                        fontSize: "0.85rem",
                        color: "#393939",
                        marginBottom: "0.4rem",
                      }}
                    >
                      <em>{c.resultadoEsperado}</em>
                    </p>
                    <ul style={{ marginBottom: "0.5rem" }}>
                      {c.descricoes.map((desc, i) => (
                        <li key={i} className="mb-1" style={{ fontSize: "0.85rem" }}>
                          {desc}
                        </li>
                      ))}
                    </ul>
                  </Column>
                  <Column sm={12} md={4} lg={3}>
                    <TextInput
                      id={c.code}
                      labelText="Pontuação (0–20)"
                      placeholder="0–20"
                      {...register(c.code)}
                    />
                    <p className="field-error">{errors[c.code]?.message}</p>
                  </Column>
                  <Column sm={12} md={8} lg={9}>
                    <TextInput
                      id={c.code + "Remarks"}
                      labelText="Comentários"
                      placeholder="Comentários (opcional)"
                      maxLength="255"
                      {...register(c.code + "Remarks")}
                    />
                  </Column>
                </Row>
              </div>
            ))}
          </div>
        ))}

        <div
          style={{
            background: "#f4f4f4",
            padding: "1rem",
            borderLeft: "4px solid #0f62fe",
            marginTop: "1.5rem",
            marginBottom: "1.5rem",
          }}
        >
          <strong>Pontuação Total Ponderada: </strong>
          <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
            {liveTotal.toFixed(2)} / 100
          </span>
        </div>

        <div className="mt-7">&nbsp;</div>
        <div className="form-buttons">
          <ButtonSet>
            <CancelButton icon={Undo24} label="Voltar" />
            <Button type="submit">Guardar Pontuação</Button>
          </ButtonSet>
        </div>
      </Form>
    </Card>
  );
};

export default ScorePrototypePage;
